// Cost sheet change log: what gets recorded when a cost sheet is edited, and how
// recorded entries (including older, noisier ones) are turned into readable rows.
//
// Field names stored in costsheet_interactions.field_name:
//   salesPOC                          top-level field
//   costSummary.transportationGST     GST of a subtotal
//   items[0]{Dell Laptop}.sp          line item field (product name kept for context)
//   items[0]{Dell Laptop}             line item added / removed
//   customRows[1]{Packing}.customGST  custom charge field
// Older entries use items[0].sp and dump whole objects (costSummary, customRows,
// brand…) as Java map strings; describeLog() understands both.

import { format } from "date-fns";

// ── Labels ───────────────────────────────────────────────────────────────────
const TOP_LABELS = {
  costSheetType: "Cost Sheet Type", sampleStatus: "Sample Status", costsheetfor: "Cost Sheet For",
  createdBy: "Created By", date: "Order Date", deliveryDate: "Delivery Date", clientName: "Client",
  clientPaymentTerms: "Client Payment Terms", salesOrderNo: "Sales Order No", salesPOC: "Sales POC",
  scmspoc: "SCM POC", costSheetRemarks: "Remarks", orderStatus: "Order Status", printing: "Printing",
  location: "Location", productMockup: "Product Mockup", remarks: "Approval Remarks",
  pending: "Pending Approval", internalCategory: "Internal Category",
  transportationTotalPp: "Transportation Cost", transportationTotalSp: "Transportation Price",
  customizationTotalPp: "Customization Cost", customizationTotalSp: "Customization Price",
  marketingTotalPp: "Marketing Cost", marketingTotalSp: "Marketing Price",
  transportationGST: "Transportation GST", customizationGST: "Customization GST", marketingGST: "Marketing GST",
};
const ITEM_LABELS = {
  details: "Product", customDetails: "Product Notes", qty: "Quantity", gst: "GST", pp: "Purchase Price",
  sp: "Selling Price", vendorName: "Vendor", vendorPaymentTerms: "Vendor Payment Terms",
  poNumber: "PO Number", esin: "ESIN", ecomProductName: "E-commerce Product",
  brand: "Brand", category: "Category", subcategory: "Subcategory",
};
const CUSTOM_LABELS = {
  customLabel: "Charge", customDetails: "Details", customGST: "GST", customPP: "Cost", customSP: "Price",
  customTotalPp: "Total Cost", customTotalSp: "Total Price", customVendorTerms: "Vendor Terms",
};

// Internal or derived values — never shown, never recorded.
const HIDDEN_TOP = new Set([
  "id", "items", "interactionLogs", "createdAT", "updatedAT", "statusUpdatedAt", "userEmail",
  "salesPocCity", "salesPocState", "approvedBy", "legalClientId", "totalPP", "totalSP", "totalMargin",
  "totalMarginPercent", "transportationDetails", "customizationDetails", "marketingDetails",
  "salesInvoiceFiles", "purchaseInvoiceBillsList", "costSummary", "customRows",
]);
const HIDDEN_ITEM = new Set([
  "id", "item", "nlc", "nsp", "totalPp", "totalSp", "margin", "marginPercent", "totPPGST", "totSPGST",
  "samples", "legalVendorId",
]);
const HIDDEN_CUSTOM = new Set(["id", "item", "customPPGST", "customSPGST"]);

const MONEY = new Set([
  "pp", "sp", "transportationTotalPp", "transportationTotalSp", "customizationTotalPp", "customizationTotalSp",
  "marketingTotalPp", "marketingTotalSp", "customPP", "customSP", "customTotalPp", "customTotalSp",
]);
const PERCENT = new Set(["gst", "customGST", "transportationGST", "customizationGST", "marketingGST"]);
const GST_KEYS = ["transportationGST", "customizationGST", "marketingGST"];
// Only these are compared and shown as numbers. Everything else — order no, PO no,
// ESIN, phone numbers — is kept exactly as entered, even when it looks numeric.
const NUMERIC = new Set([...MONEY, ...PERCENT, "qty"]);

const humanize = (key) => key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());

// ── Value parsing / comparison ───────────────────────────────────────────────
const isBlank = (v) => v == null || (typeof v === "string" && ["", "N/A", "null", "undefined"].includes(v.trim()));

// Java's Map/List toString ("{id=3, name=Foo}", "[{a=1}, {a=2}]") as written by older logs.
const parseJavaMap = (s) => {
  const body = s.trim().slice(1, -1);
  if (!body) return {};
  const out = {};
  body.split(/, (?=[A-Za-z_][A-Za-z0-9_]*=)/).forEach((pair) => {
    const i = pair.indexOf("=");
    if (i > 0) out[pair.slice(0, i)] = pair.slice(i + 1);
  });
  return out;
};
const parseStructured = (v) => {
  if (typeof v !== "string") return v;
  const s = v.trim();
  if (!/^[[{]/.test(s)) return v;
  try { return JSON.parse(s); } catch { /* not JSON */ }
  if (s.startsWith("{")) return parseJavaMap(s);
  const body = s.slice(1, -1).trim();
  if (!body) return [];
  return body.replace(/^\{|\}$/g, "").split(/\}, \{/).map((m) => parseJavaMap(`{${m}}`));
};

const asNumber = (v) => {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "" && !isNaN(Number(v))) return Number(v);
  return null;
};

// Reduces a value to what a person would compare: names for catalog objects, numbers for numeric fields.
const comparable = (v, key) => {
  const p = parseStructured(v);
  if (isBlank(p)) return null;
  if (p && typeof p === "object" && !Array.isArray(p)) {
    if (p.name != null) return String(p.name);
    if (p.id != null) return `#${p.id}`;
    return JSON.stringify(p);
  }
  if (NUMERIC.has(key)) {
    const n = asNumber(p);
    if (n != null) return n;
  }
  return String(p).trim();
};

const sameValue = (a, b, key) => {
  const pa = parseStructured(a), pb = parseStructured(b);
  // Catalog objects: an id-only stub and the full object are the same thing.
  const ida = pa && typeof pa === "object" ? pa.id : undefined;
  const idb = pb && typeof pb === "object" ? pb.id : undefined;
  if (ida != null && idb != null) return String(ida) === String(idb);
  return comparable(a, key) === comparable(b, key);
};

const inr = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const TERMS = { advance: "Advance", "against-delivery": "Against Delivery" };

export const formatValue = (key, v) => {
  const c = comparable(v, key);
  if (c == null) return null;
  if (typeof c === "number") {
    if (MONEY.has(key)) return `${c < 0 ? "-" : ""}₹${inr.format(Math.abs(c))}`;
    if (PERCENT.has(key)) return `${c}%`;
    return String(c);
  }
  if (c.startsWith("#")) return null; // catalog id with no name — nothing meaningful to show
  if (key === "vendorPaymentTerms" || key === "clientPaymentTerms") {
    return TERMS[c] || c.replace(/^(\d+)-days$/, "$1 days");
  }
  if (key === "pending") return c === "true" ? "Yes" : c === "false" ? "No" : c;
  if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(c)) {
    const d = new Date(c);
    if (!isNaN(d)) return format(d, "dd MMM yyyy");
  }
  return c;
};

// ── Reading logs ─────────────────────────────────────────────────────────────
const SCOPED = /^(items|customRows)\[(\d+)\](?:\{(.*)\})?(?:\.([A-Za-z0-9_]+))?$/;

const row = (section, key, label, oldValue, newValue) => ({
  section, label,
  oldValue: formatValue(key, oldValue),
  newValue: formatValue(key, newValue),
});

/**
 * Turns one stored log into zero or more display rows:
 *   { section: { id, kind: 'sheet'|'item'|'custom', title, subtitle }, label, oldValue, newValue, note? }
 * Rows that aren't real changes (5 → 5.0, id stub vs object, internal keys) are dropped.
 * `currentItems` names line items in older logs that only stored an index.
 */
export const describeLog = (log, currentItems = []) => {
  const field = log.fieldName || "";
  const sheet = { id: "sheet", kind: "sheet", title: "Cost sheet details" };

  const scoped = field.match(SCOPED);
  if (scoped) {
    const [, collection, idxStr, storedName, key] = scoped;
    const idx = parseInt(idxStr, 10);
    if (collection === "items") {
      const name = storedName || currentItems[idx]?.details || "";
      const section = { id: `item-${idx}`, kind: "item", title: `Line ${idx + 1}`, subtitle: name };
      if (!key) {
        const added = isBlank(log.oldValue);
        return [{ section, label: added ? "Line item added" : "Line item removed", note: added ? "added" : "removed",
          oldValue: null, newValue: null }];
      }
      if (HIDDEN_ITEM.has(key) || sameValue(log.oldValue, log.newValue, key)) return [];
      // Older logs stored catalog picks as id-only stubs; name them from the current line if it matches.
      const named = (v) => {
        const p = parseStructured(v), cur = currentItems[idx]?.[key];
        return p && typeof p === "object" && p.name == null && cur?.id != null && String(cur.id) === String(p.id) ? cur : v;
      };
      return [row(section, key, ITEM_LABELS[key] || humanize(key), named(log.oldValue), named(log.newValue))];
    }
    const section = { id: `custom-${idx}`, kind: "custom", title: `Custom charge ${idx + 1}`, subtitle: storedName || "" };
    if (!key) {
      const added = isBlank(log.oldValue);
      return [{ section, label: added ? "Charge added" : "Charge removed", note: added ? "added" : "removed",
        oldValue: null, newValue: null }];
    }
    if (HIDDEN_CUSTOM.has(key) || sameValue(log.oldValue, log.newValue, key)) return [];
    return [row(section, key, CUSTOM_LABELS[key] || humanize(key), log.oldValue, log.newValue)];
  }

  const dotted = field.match(/^costSummary\.([A-Za-z]+)$/);
  if (dotted) {
    const key = dotted[1];
    return sameValue(log.oldValue, log.newValue, key) ? [] : [row(sheet, key, TOP_LABELS[key] || humanize(key), log.oldValue, log.newValue)];
  }

  // Older logs: whole costSummary map in one entry.
  if (field === "costSummary") {
    const o = parseStructured(log.oldValue) || {}, n = parseStructured(log.newValue) || {};
    return GST_KEYS.filter((k) => !sameValue(o[k], n[k], k))
      .map((k) => row(sheet, k, TOP_LABELS[k], o[k], n[k]));
  }

  // Older logs: whole customRows list in one entry.
  if (field === "customRows") {
    const o = parseStructured(log.oldValue), n = parseStructured(log.newValue);
    const oldRows = Array.isArray(o) ? o : [], newRows = Array.isArray(n) ? n : [];
    const rows = [];
    for (let i = 0; i < Math.max(oldRows.length, newRows.length); i++) {
      const before = oldRows[i], after = newRows[i];
      const section = { id: `custom-${i}`, kind: "custom", title: `Custom charge ${i + 1}`,
        subtitle: (after || before)?.customLabel || "" };
      if (!before || !after) {
        rows.push({ section, label: before ? "Charge removed" : "Charge added", note: before ? "removed" : "added",
          oldValue: null, newValue: null });
        Object.keys(CUSTOM_LABELS).forEach((k) => {
          const v = (before || after)[k];
          if (!isBlank(v)) rows.push(row(section, k, CUSTOM_LABELS[k], before ? v : null, after ? v : null));
        });
        continue;
      }
      Object.keys(CUSTOM_LABELS).forEach((k) => {
        if (!sameValue(before[k], after[k], k)) rows.push(row(section, k, CUSTOM_LABELS[k], before[k], after[k]));
      });
    }
    return rows;
  }

  if (HIDDEN_TOP.has(field) || sameValue(log.oldValue, log.newValue, field)) return [];
  return [row(sheet, field, TOP_LABELS[field] || humanize(field), log.oldValue, log.newValue)];
};

// ── Writing logs ─────────────────────────────────────────────────────────────
/**
 * Compares the cost sheet as opened with what is being saved and returns one log
 * per field a person actually changed.
 *  original    – the cost sheet as prepared when the edit dialog opened
 *  edited      – the cost sheet in the dialog at save time (full catalog objects)
 *  resolveTerms(item, i, which) – vendor payment terms for a line ('custom' resolved)
 */
export const buildChangeLogs = ({ original, edited, resolveTerms, changedBy }) => {
  const changedAt = new Date().toISOString();
  const logs = [];
  const push = (fieldName, oldValue, newValue) => {
    const key = fieldName.split(".").pop();
    if (sameValue(oldValue, newValue, key)) return;
    logs.push({
      fieldName,
      oldValue: comparable(oldValue, key) ?? "N/A",
      newValue: comparable(newValue, key) ?? "N/A",
      changedBy, changedAt,
    });
  };
  const nameOf = (s) => String(s || "").replace(/[{}]/g, "").trim();

  Object.keys({ ...original, ...edited }).forEach((k) => {
    if (HIDDEN_TOP.has(k) || GST_KEYS.includes(k)) return;
    push(k, original[k], edited[k]);
  });

  GST_KEYS.forEach((k) => push(`costSummary.${k}`, original[k], edited[k]));

  const oldItems = original.items || [], newItems = edited.items || [];
  for (let i = 0; i < Math.max(oldItems.length, newItems.length); i++) {
    const before = oldItems[i], after = newItems[i];
    const name = nameOf((after || before)?.details);
    const prefix = `items[${i}]{${name}}`;
    if (!before || !after) {
      logs.push({ fieldName: prefix, oldValue: before ? name || "Line item" : "N/A",
        newValue: after ? name || "Line item" : "N/A", changedBy, changedAt });
    }
    // For an added/removed line the missing side is empty, so every value it had is recorded.
    Object.keys(ITEM_LABELS).forEach((k) => {
      if (k === "vendorPaymentTerms") {
        push(`${prefix}.${k}`, before && resolveTerms(before, i, "old"), after && resolveTerms(after, i, "new"));
      } else push(`${prefix}.${k}`, before?.[k], after?.[k]);
    });
  }

  const oldRows = original.customRows || [], newRows = edited.customRows || [];
  for (let i = 0; i < Math.max(oldRows.length, newRows.length); i++) {
    const before = oldRows[i], after = newRows[i];
    const label = nameOf((after || before)?.customLabel);
    const prefix = `customRows[${i}]{${label}}`;
    if (!before || !after) {
      logs.push({ fieldName: prefix, oldValue: before ? label || "Charge" : "N/A",
        newValue: after ? label || "Charge" : "N/A", changedBy, changedAt });
    }
    Object.keys(CUSTOM_LABELS).forEach((k) => push(`${prefix}.${k}`, before?.[k], after?.[k]));
  }

  return logs;
};
